/** Count visual lines: hard breaks plus wrapping of lines over `charsPerLine`. */
const countVisualLines = (value: string, charsPerLine: number): number =>
  value.split('\n').reduce((total, line) => {
    const wrapped = Math.max(1, Math.ceil(line.length / charsPerLine));
    return total + wrapped;
  }, 0);

/** Number of hard line breaks in a value. */
const countNewlines = (value: string): number => value.split('\n').length - 1;

/**
 * Fill percent (0–100) of a value against a line budget and/or char cap,
 * whichever is closer to full. Line progress counts completed visual rows plus
 * how full the current row is, so it eases toward 100% and only reaches it when
 * the last row is full too. Returns 0 when neither budget is configured.
 */
export const computeFillPercent = (
  value: string,
  charsPerLine: number,
  maxLines?: number,
  maxLength?: number,
): number => {
  const linesUsed =
    maxLines != null ? countVisualLines(value, charsPerLine) : 0;
  // Chars on the current visual row: the text after the last hard break, then
  // modulo the wrap width so soft-wrapped rows count too. A non-empty exact
  // multiple means the last row is full, not empty.
  const lastSegment = value.slice(value.lastIndexOf('\n') + 1);
  const remainder = lastSegment.length % charsPerLine;
  const charsOnCurrentRow =
    remainder === 0 && lastSegment.length > 0 ? charsPerLine : remainder;
  const currentLineFill = charsOnCurrentRow / charsPerLine;
  const linePercent =
    maxLines != null && maxLines > 0
      ? ((linesUsed - 1 + currentLineFill) / maxLines) * 100
      : 0;
  const charPercent =
    maxLength != null && maxLength > 0 ? (value.length / maxLength) * 100 : 0;
  const raw = Math.max(0, Math.min(100, Math.max(linePercent, charPercent)));
  // Floor so the counter ticks down on the very first character removed near a
  // boundary (Math.round could hold the same value for a keystroke or two).
  // 100 is reserved for a genuinely full value, not a floored 99.x.
  return raw >= 100 ? 100 : Math.floor(raw);
};

/** Trim from the end until the value fits within `maxLines` visual lines. */
export const clampToLineBudget = (
  value: string,
  maxLines: number,
  charsPerLine: number,
): string => {
  if (countVisualLines(value, charsPerLine) <= maxLines) {
    return value;
  }
  // Binary-search the largest end-trimmed prefix that fits (countVisualLines
  // is monotonic in prefix length), avoiding an O(n²) per-character drop-loop.
  let lo = 0;
  let hi = value.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (countVisualLines(value.slice(0, mid), charsPerLine) <= maxLines) {
      lo = mid;
    } else {
      hi = mid - 1;
    }
  }
  return value.slice(0, lo);
};

/**
 * Whether inserting a newline pushed the value past `maxLines` when the same
 * text without hard breaks would still fit. Used to block a break that would
 * otherwise force end-trimming of existing content.
 */
export const newlineOverflowsBudget = (
  rawValue: string,
  prevValue: string,
  maxLines: number,
  charsPerLine: number,
): boolean =>
  countNewlines(rawValue) > countNewlines(prevValue) &&
  countVisualLines(rawValue, charsPerLine) > maxLines &&
  countVisualLines(rawValue.replaceAll('\n', ' '), charsPerLine) <= maxLines;
